"use client";

import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import {
  Images,
  Loader2,
  MessageCircle,
  Search,
  ShieldCheck,
  Star,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { listPublicReviews, type ReviewListResponse } from "@/actions/review";
import { AppImage as Image } from "@/components/ui/app-image";
import { firstMediaPreviewUrl, mediaUrlFromAsset } from "@/lib/media";
import { cn } from "@/lib/utils";
import { ReviewCard } from "./review-card.client";
import { ReviewPageShell } from "./review-page-shell";
import { ReviewSummary } from "./review-summary";
import { ReviewWriteButton } from "./review-write-button.client";

type ReviewListTarget =
  | { targetType: "hiive"; hiiveSlug: string }
  | { targetType: "venue"; hiiveSlug: string; venueSlug: string };

type ReviewListPageProps = {
  target: ReviewListTarget;
  initialData?: ReviewListResponse;
  title: string;
  writeHref: string;
  backHref: string;
  backLabel?: string;
  isAuthenticated?: boolean;
  inline?: boolean;
  reviewBaseHref?: string;
};

const sortOptions = [
  ["relevant", "Relevant"],
  ["newest", "Newest"],
  ["highest", "Highest"],
  ["lowest", "Lowest"],
  ["helpful", "Helpful"],
] as const;

// Shared liquid-glass surface tokens.
const GLASS_PANEL =
  "rounded-[24px] border border-white/15 bg-white/40 shadow-lg backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[inset_0_1px_1px_rgba(255,255,255,0.06)]";

type TopicFilter = {
  key: string;
  label: string;
  count: number;
  icon: typeof Star;
};

function buildTopicFilters(
  aggregate: ReviewListResponse["aggregate"],
): TopicFilter[] {
  const lowCount = Object.entries(aggregate.histogram).reduce(
    (total, [rating, count]) => (Number(rating) <= 2 ? total + count : total),
    0,
  );
  const staticTopics: Array<TopicFilter | null> = [
    aggregate.mediaCount > 0
      ? {
          key: "with_media",
          label: "Photos",
          count: aggregate.mediaCount,
          icon: Images,
        }
      : null,
    aggregate.ownerResponseCount > 0
      ? {
          key: "owner_replied",
          label: "Replied",
          count: aggregate.ownerResponseCount,
          icon: MessageCircle,
        }
      : null,
    aggregate.verifiedCount > 0
      ? {
          key: "verified",
          label: "Verified",
          count: aggregate.verifiedCount,
          icon: ShieldCheck,
        }
      : null,
    lowCount > 0
      ? { key: "low_rating", label: "Low rating", count: lowCount, icon: Star }
      : null,
  ];

  const subratingTopics: TopicFilter[] = Object.entries(aggregate.topicCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([key, count]) => ({
      key,
      label: key.replace(/_/g, " "),
      count,
      icon: Star,
    }));

  return [
    ...staticTopics.filter((item): item is TopicFilter => Boolean(item)),
    ...subratingTopics,
  ];
}

function Chip({
  active,
  onClick,
  children,
  className,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium capitalize backdrop-blur-md transition active:scale-95",
        active
          ? "border-white/25 bg-white/70 text-text-primary shadow-sm dark:border-white/25 dark:bg-white/15 dark:text-white"
          : "border-white/15 bg-white/30 text-text-secondary hover:bg-white/50 dark:border-white/10 dark:bg-white/[0.04] dark:text-white/70 dark:hover:bg-white/[0.08]",
        className,
      )}
    >
      {children}
    </button>
  );
}

function ReviewCardSkeleton() {
  return (
    <div className={cn(GLASS_PANEL, "space-y-4 p-4")}>
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 animate-pulse rounded-xl bg-white/40 dark:bg-white/10" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 w-1/3 animate-pulse rounded-full bg-white/40 dark:bg-white/10" />
          <div className="h-3 w-1/4 animate-pulse rounded-full bg-white/30 dark:bg-white/[0.06]" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3 w-full animate-pulse rounded-full bg-white/30 dark:bg-white/[0.06]" />
        <div className="h-3 w-5/6 animate-pulse rounded-full bg-white/30 dark:bg-white/[0.06]" />
      </div>
    </div>
  );
}

export function ReviewListPage({
  target,
  initialData,
  title,
  writeHref,
  backHref,
  backLabel = "Back",
  isAuthenticated = false,
  inline = false,
  reviewBaseHref,
}: ReviewListPageProps) {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 350);
  const q = debouncedSearch.trim() ? debouncedSearch.trim() : null;
  const [sort, setSort] = useState<string>("relevant");
  const [rating, setRating] = useState<number | null>(null);
  const [topic, setTopic] = useState<string | null>(null);

  const isDefaultFilters =
    sort === "relevant" && rating == null && !q && topic == null;

  const {
    data: pagedData,
    isFetching,
    isPending,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "reviews",
      target.targetType,
      target.hiiveSlug,
      target.targetType === "venue" ? target.venueSlug : null,
      sort,
      rating,
      q,
      topic,
    ],
    queryFn: async ({ pageParam }) => {
      const result = await listPublicReviews({
        ...target,
        sort,
        rating,
        q,
        topic,
        cursor: pageParam,
      });
      if (!result.ok) throw new Error(result.message);
      return result.data;
    },
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : null,
    // Seed the default view from the server so the first paint is instant and
    // does not fire a client-side server action during navigation.
    initialData:
      isDefaultFilters && initialData
        ? { pages: [initialData], pageParams: [null] }
        : undefined,
    // Keep showing the previous results (slightly dimmed) while a new filter
    // loads, so changing a filter never blanks the screen.
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  // Flatten the cursor pages into one list; aggregate/eligibility/target come
  // from the first page and are identical across pages.
  const data = useMemo(() => {
    const firstPage = pagedData?.pages[0];
    if (!firstPage) return undefined;
    return {
      ...firstPage,
      reviews: pagedData.pages.flatMap((page) => page.reviews),
    };
  }, [pagedData]);

  const detailBaseHref =
    reviewBaseHref ??
    (target.targetType === "venue"
      ? `/hiives/${target.hiiveSlug}/venues/${target.venueSlug}/reviews`
      : `/hiives/${target.hiiveSlug}/reviews`);

  // Loading the next page appends below the list, so it should not dim the
  // already-visible reviews the way a filter refetch does.
  const isRefreshing = isFetching && !isPending && !isFetchingNextPage;
  const hasActiveFilters = !isDefaultFilters;

  const content = data ? (
    <ReviewListContent
      data={data}
      title={title}
      writeHref={writeHref}
      isAuthenticated={isAuthenticated}
      inline={inline}
      detailBaseHref={detailBaseHref}
      searchInput={searchInput}
      onSearchInput={setSearchInput}
      sort={sort}
      onSort={setSort}
      rating={rating}
      onRating={setRating}
      topic={topic}
      onTopic={setTopic}
      isRefreshing={isRefreshing}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={() => {
        setSearchInput("");
        setSort("relevant");
        setRating(null);
        setTopic(null);
      }}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onLoadMore={() => void fetchNextPage()}
    />
  ) : isError ? (
    <div className={cn(GLASS_PANEL, "mx-4 mt-4 p-8 text-center")}>
      <p className="text-sm text-text-secondary dark:text-white/60">
        Could not load reviews right now.
      </p>
      <button
        type="button"
        onClick={() => void refetch()}
        className="mt-3 rounded-full border border-white/20 bg-white/40 px-4 py-2 text-sm font-medium backdrop-blur-md dark:bg-white/[0.06]"
      >
        Retry
      </button>
    </div>
  ) : (
    <div className="space-y-3 px-4 py-4">
      <ReviewCardSkeleton />
      <ReviewCardSkeleton />
      <ReviewCardSkeleton />
    </div>
  );

  if (inline) {
    return <div>{content}</div>;
  }

  return (
    <ReviewPageShell
      title={title}
      headerTitle="Reviews"
      backFallbackHref={backHref}
      backLabel={backLabel}
    >
      {content}
    </ReviewPageShell>
  );
}

function ReviewListContent({
  data,
  title,
  writeHref,
  isAuthenticated,
  inline,
  detailBaseHref,
  searchInput,
  onSearchInput,
  sort,
  onSort,
  rating,
  onRating,
  topic,
  onTopic,
  isRefreshing,
  hasActiveFilters,
  onClearFilters,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  data: ReviewListResponse;
  title: string;
  writeHref: string;
  isAuthenticated: boolean;
  inline: boolean;
  detailBaseHref: string;
  searchInput: string;
  onSearchInput: (value: string) => void;
  sort: string;
  onSort: (value: string) => void;
  rating: number | null;
  onRating: (value: number | null) => void;
  topic: string | null;
  onTopic: (value: string | null) => void;
  isRefreshing: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const { aggregate, eligibility, target } = data;
  const canReview = Boolean(
    eligibility?.allowed || eligibility?.existingReviewPublicId,
  );
  const blockedReason = eligibility?.reason ?? "Sign in to review this page.";
  const targetName =
    target.targetType === "venue" ? target.venueName : target.hiiveName;
  // Hiives show their square (1:1) logo; venues keep a wide banner. The hiive
  // banner is often a video, which can't render as an <img>, so the logo is the
  // reliable, on-brand header image.
  const logoUrl =
    target.targetType === "hiive"
      ? mediaUrlFromAsset(
          target.logoImage as Parameters<typeof mediaUrlFromAsset>[0],
        )
      : null;
  const bannerUrl =
    target.targetType === "venue" && target.bannerMedia
      ? firstMediaPreviewUrl(target.bannerMedia)
      : null;
  const topics = buildTopicFilters(aggregate);

  const stickyTopClass = inline
    ? "top-14"
    : "top-[calc(env(safe-area-inset-top)+3.5rem)]";

  return (
    <>
      {!inline && (
        <div className="space-y-4 px-4 pt-4 pb-2">
          {target.targetType === "hiive" ? (
            logoUrl ? (
              <div className="relative h-24 w-24 overflow-hidden rounded-[24px] border border-white/15 shadow-lg dark:border-white/10">
                <Image
                  src={logoUrl}
                  alt={targetName}
                  fill
                  className="object-cover"
                  sizes="96px"
                  priority
                />
              </div>
            ) : null
          ) : bannerUrl ? (
            <div className="relative aspect-[21/9] overflow-hidden rounded-[24px] border border-white/10 shadow-lg sm:aspect-[24/9]">
              <Image
                src={bannerUrl}
                alt={targetName}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 768px"
                priority
              />
            </div>
          ) : null}
          <h1 className="text-2xl font-bold tracking-tight text-text-primary sm:text-3xl dark:text-white">
            {targetName}
          </h1>
        </div>
      )}

      <div className={cn("px-4", inline ? "pt-4" : "pt-1")}>
        <ReviewWriteButton
          canReview={canReview}
          writeHref={writeHref}
          writeLabel={
            eligibility?.existingReviewPublicId
              ? "Edit your review"
              : "Write a review"
          }
          blockedReason={blockedReason}
          isAuthenticated={isAuthenticated}
          size="lg"
          className="w-full"
        />
      </div>

      <div className="px-4 pt-3">
        <ReviewSummary
          title={title}
          subtitle=""
          writeHref={writeHref}
          aggregate={aggregate}
          eligibility={eligibility}
          activeRating={rating}
          onSelectRating={onRating}
          isAuthenticated={isAuthenticated}
          showWriteButton={false}
        />
      </div>

      {/* Liquid-glass filter bar — debounced search + sort + topic chips */}
      <div className={cn("sticky z-40 mx-4 mt-3", stickyTopClass)}>
        <div className={cn(GLASS_PANEL, "space-y-3 rounded-[20px] p-3")}>
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3.5 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
            <input
              type="text"
              value={searchInput}
              onChange={(event) => onSearchInput(event.target.value)}
              placeholder="Search reviews"
              className="h-11 w-full rounded-full border border-white/20 bg-white/40 pr-10 pl-10 text-base text-text-primary transition outline-none placeholder:text-text-tertiary focus:border-white/40 focus:bg-white/60 md:text-sm dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:focus:bg-white/[0.08]"
            />
            {searchInput ? (
              <button
                type="button"
                onClick={() => onSearchInput("")}
                aria-label="Clear search"
                className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full p-1 text-text-tertiary transition hover:text-text-primary"
              >
                <X className="h-4 w-4" />
              </button>
            ) : (
              <Loader2
                className={cn(
                  "absolute top-1/2 right-3.5 h-4 w-4 -translate-y-1/2 animate-spin text-text-tertiary transition-opacity",
                  isRefreshing ? "opacity-100" : "opacity-0",
                )}
              />
            )}
          </div>

          <div className="scrollbar-hide -mx-1 flex gap-2 overflow-x-auto px-1">
            {sortOptions.map(([value, label]) => (
              <Chip
                key={value}
                active={sort === value}
                onClick={() => onSort(value)}
              >
                {label}
              </Chip>
            ))}
          </div>

          {topics.length > 0 ? (
            <div className="scrollbar-hide -mx-1 flex gap-2 overflow-x-auto px-1">
              {hasActiveFilters ? (
                <Chip active={false} onClick={onClearFilters}>
                  <X className="h-3.5 w-3.5" />
                  Clear
                </Chip>
              ) : null}
              {topics.map((item) => {
                const Icon = item.icon;
                return (
                  <Chip
                    key={item.key}
                    active={topic === item.key}
                    onClick={() =>
                      onTopic(topic === item.key ? null : item.key)
                    }
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {item.label}
                    <span className="tabular-nums opacity-65">
                      {item.count}
                    </span>
                  </Chip>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={cn(
          "space-y-3 px-4 py-4 transition-opacity duration-200",
          isRefreshing && "pointer-events-none opacity-50",
        )}
      >
        {data.reviews.length > 0 ? (
          <>
            {data.reviews.map((review, index) => (
              <div
                key={review.publicId}
                className="motion-safe:animate-in motion-safe:duration-500 motion-safe:fade-in motion-safe:slide-in-from-bottom-3"
                style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
              >
                <ReviewCard
                  review={review}
                  permalink={`${detailBaseHref}/${review.publicId}`}
                  isAuthenticated={isAuthenticated}
                />
              </div>
            ))}
            {hasNextPage ? (
              <button
                type="button"
                onClick={onLoadMore}
                disabled={isFetchingNextPage}
                className={cn(
                  GLASS_PANEL,
                  "flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-medium text-text-primary transition active:scale-[0.99] disabled:opacity-60 dark:text-white",
                )}
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading reviews
                  </>
                ) : (
                  "Show more reviews"
                )}
              </button>
            ) : null}
          </>
        ) : (
          <div className={cn(GLASS_PANEL, "p-8 text-center")}>
            <h2 className="text-lg font-semibold text-text-primary dark:text-white">
              {hasActiveFilters ? "No matching reviews" : "No reviews yet"}
            </h2>
            <p className="mt-2 text-sm text-text-secondary dark:text-white/60">
              {hasActiveFilters
                ? "Try clearing a filter or searching for something else."
                : "Reviews from verified bookings, events, and members will appear here."}
            </p>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={onClearFilters}
                className="mt-4 rounded-full border border-white/20 bg-white/40 px-4 py-2 text-sm font-medium backdrop-blur-md dark:bg-white/[0.06]"
              >
                Clear filters
              </button>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}
